// Wave UX-2.1 Item 4 (2026-05-23) — 마감 임박 알림 bell + 모달.
// Layout 헤더에 마운트. 60s 폴링 + Browser Notification API 옵션.
//
// 데이터 소스:
//   GET /buyer-app/me/favorites — 즐겨찾기 차량의 endsAt
//   GET /buyer-app/me/bids      — 본인 입찰 차량 (myBid.status='active' 만)
//
// 분류:
//   D-10m → urgent (빨강 강조 + Browser Notification 발송)
//   D-1h  → soon  (노랑)
//   later → 모달에서만 표시 안 함
//
// 디바운스: 같은 (auctionId, bucket) 조합 — 30분 안에 중복 푸시 안 함 (localStorage).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import { useT } from '../../i18n/I18nProvider';
import { buildEndingSoonList, formatRemaining } from './endingSoon';

var POLL_MS = 60 * 1000;
var DEDUP_MS = 30 * 60 * 1000;
var DEDUP_KEY = 'autodex_buyer_notif_dedup';

// perf/bell-fetch-cache (2026-05-24) — Layout 페이지별 재마운트로 인한 fetch 폭증 차단.
// Layout 이 Route element 안에 wrap 되어 매 navigate 마다 unmount/remount → NotificationBell 도 동일.
// 캐시: 최근 fetch < THROTTLE_MS 면 즉시 fetch skip + interval 만 셋업. 사용자는 캐시 items 즉시 봄.
var THROTTLE_MS = 30 * 1000;
var CACHE_KEY = 'autodex_buyer_notif_cache';

function loadCache() {
  try {
    var raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.t !== 'number' || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch (e) { return null; }
}
function saveCache(items) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), items: items })); } catch (e) {}
}

function loadDedup() {
  try { return JSON.parse(localStorage.getItem(DEDUP_KEY) || '{}') || {}; } catch (e) { return {}; }
}
function saveDedup(d) {
  try { localStorage.setItem(DEDUP_KEY, JSON.stringify(d)); } catch (e) {}
}
function pruneDedup(d, now) {
  var out = {};
  Object.keys(d).forEach(function(k){ if (now - d[k] < DEDUP_MS) out[k] = d[k]; });
  return out;
}

// Browser Notification 발송 (권한 granted 시만)
function fireBrowserNotif(title, body, onClickPath, navigate) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    var n = new Notification(title, { body: body, tag: 'autodex-buyer-notif' });
    n.onclick = function() {
      window.focus();
      if (onClickPath && navigate) navigate(onClickPath);
      n.close();
    };
  } catch (e) {}
}

function NotificationBell() {
  var t = useT();
  var navigate = useNavigate();
  // perf/bell-fetch-cache (2026-05-24) — 캐시된 items 즉시 렌더 (stale-while-revalidate)
  var [items, setItems] = useState(function(){
    var c = loadCache();
    return c ? c.items : [];
  });
  var [open, setOpen] = useState(false);
  var [permState, setPermState] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  var pollerRef = useRef(null);
  // fix/notif-bell-t-dep (2026-05-24) — t 가 locale 변경 시 재생성 → fetchSources 재생성 → polling re-init 회피.
  // tRef 로 latest 값을 캡처 — fetchSources 의존성에서 t 제거.
  var tRef = useRef(t);
  useEffect(function(){ tRef.current = t; }, [t]);

  var fetchSources = useCallback(function() {
    var hitAuth = false;
    function checkAuth(r) {
      if (isUnauthorized(r)) { hitAuth = true; throw new Error('unauthorized'); }
      return r.json();
    }
    Promise.allSettled([
      authBuyerFetch(API + '/buyer-app/me/favorites', {}).then(checkAuth),
      authBuyerFetch(API + '/buyer-app/me/bids', {}).then(checkAuth),
    ]).then(function(results) {
      if (hitAuth) { handleUnauthorized(navigate); return; }
      var favRes = results[0].status === 'fulfilled' ? results[0].value : null;
      var bidsRes = results[1].status === 'fulfilled' ? results[1].value : null;
      var favorites = (favRes && favRes.success && favRes.data && favRes.data.auctions) || [];
      var bids = (bidsRes && bidsRes.success && bidsRes.data && bidsRes.data.bids) || [];
      var list = buildEndingSoonList(favorites, bids, Date.now());
      setItems(list);
      saveCache(list);

      // Urgent (10m) bucket → Browser Notification (디바운스 30분)
      var dedup = pruneDedup(loadDedup(), Date.now());
      var changed = false;
      list.forEach(function(it) {
        if (it.bucket !== '10m') return;
        var k = it.id + ':' + it.bucket;
        if (dedup[k]) return;
        dedup[k] = Date.now();
        changed = true;
        var title = tRef.current('notifEnding10m');
        var body = (it.title || '') + (it.carNumber ? ' (' + it.carNumber + ')' : '');
        fireBrowserNotif(title, body, '/buyer/auctions/' + it.id, navigate);
      });
      if (changed) saveDedup(dedup);
    });
  }, [navigate]);

  useEffect(function() {
    // perf/bell-fetch-cache (2026-05-24) — 최근 fetch < THROTTLE_MS 면 즉시 fetch skip.
    // 페이지 전환 (Layout 재마운트) 시 매번 즉시 fetch 발생하던 패턴 차단.
    // interval 은 항상 셋업 — 60s 마다 신선한 데이터 확보.
    var cached = loadCache();
    if (!cached || (Date.now() - cached.t) > THROTTLE_MS) {
      fetchSources();
    }
    pollerRef.current = setInterval(fetchSources, POLL_MS);
    return function() { if (pollerRef.current) clearInterval(pollerRef.current); };
  }, [fetchSources]);

  function requestPermission() {
    if (typeof Notification === 'undefined') return;
    Notification.requestPermission().then(function(p){ setPermState(p); });
  }

  var urgentCount = items.filter(function(i){ return i.bucket === '10m'; }).length;
  var totalCount = items.length;

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" onClick={function(){ setOpen(!open); }}
        title={t('notifBellTooltip')}
        style={{
          position: 'relative',
          height: 34, width: 38,
          background: 'white',
          border: '1.5px solid #e2e8f0', borderRadius: 8,
          fontSize: 16, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',        }}>
        <span>🔔</span>
        {totalCount > 0 && (
          <span style={{
            position: 'absolute', top: -6, right: -6,
            background: urgentCount > 0 ? '#dc2626' : '#3b82f6',
            color: 'white', fontSize: 10, fontWeight: 800,
            minWidth: 18, height: 18, borderRadius: 999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px',
            boxShadow: '0 0 0 2px white',
          }}>{totalCount}</span>
        )}
      </button>

      {open && (
        <>
          <div onClick={function(){setOpen(false);}}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100 }} />
          <div style={{
            position: 'absolute', top: 42, right: 0,
            width: 320, maxHeight: 480, overflowY: 'auto',
            background: 'white', borderRadius: 12,
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 32px rgba(15,23,42,0.12)',
            padding: 16, zIndex: 101,          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{t('notifModalTitle')}</h3>
              <button type="button" onClick={function(){setOpen(false);}}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8', padding: 0 }}>×</button>
            </div>

            {/* Browser Notification permission UI */}
            {permState === 'default' && (
              <button type="button" onClick={requestPermission}
                style={{
                  width: '100%', padding: '8px 12px', marginBottom: 12,
                  background: '#eff6ff', border: '1px solid #bfdbfe',
                  borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#1e40af',
                  cursor: 'pointer',
                }}>{t('notifBrowserPermAsk')}</button>
            )}
            {permState === 'granted' && (
              <div style={{
                padding: '6px 10px', marginBottom: 12,
                background: '#ecfdf5', borderRadius: 6,
                fontSize: 11, color: '#065f46', fontWeight: 600,
              }}>{t('notifBrowserPermGranted')}</div>
            )}
            {permState === 'denied' && (
              <div style={{
                padding: '6px 10px', marginBottom: 12,
                background: '#fef2f2', borderRadius: 6,
                fontSize: 11, color: '#991b1b', fontWeight: 600,
              }}>{t('notifBrowserPermDenied')}</div>
            )}

            {items.length === 0 ? (
              <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                {t('notifModalEmpty')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(function(it) {
                  var urgent = it.bucket === '10m';
                  return (
                    <div key={it.id}
                      onClick={function(){ setOpen(false); navigate('/buyer/auctions/' + it.id); }}
                      className="buyer-item-lift"
                      style={{
                        padding: 12,
                        background: urgent ? '#fef2f2' : '#fffbeb',
                        border: '1px solid ' + (urgent ? '#fecaca' : '#fde68a'),
                        borderRadius: 8, cursor: 'pointer',
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: urgent ? '#991b1b' : '#92400e' }}>
                          {urgent ? t('notifEnding10m') : t('notifEnding1h')}
                        </span>
                        <span style={{ fontSize: 11, color: urgent ? '#991b1b' : '#92400e', fontWeight: 600 }}>
                          {formatRemaining(it.endsAt, t)}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>
                        {it.title || (it.carNumber || '')}
                      </div>
                      <div style={{ fontSize: 10, color: '#64748b', display: 'flex', gap: 8 }}>
                        {it.source === 'favorite' || it.source === 'both' ? <span>{t('notifSourceFavorite')}</span> : null}
                        {it.source === 'bid' || it.source === 'both' ? <span>{t('notifSourceBid')}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
